import type { ChartOptionsMapType } from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/utils';
import type {
  IAdaptedOpinionLineChartData,
  IOpinionBarAndLineChartData,
  IOpinionBarAndLineFlatDatum,
  IOpinionBarAndLineSeriesData,
  IOpinionBarAndLineSeriesItem,
} from './types';

const DEFAULT_SERIES_COLORS = [
  '#5086F3',
  '#69D19C',
  '#F17C86',
  '#F8D74D',
  '#7F6FF1',
  '#5EC8F1',
  '#6E7794',
  '#AD53D4',
];

const IGNORED_SERIES_NAMES = ['TopTipsEvent', 'Total'];

function isSeriesData(
  data: IOpinionBarAndLineChartData,
): data is IOpinionBarAndLineSeriesData {
  return !Array.isArray(data) && Array.isArray(data?.series);
}

function createSeriesKey(name: string, index: number) {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return slug ? `intel_${slug}` : `intel_series_${index}`;
}

function normalizeSeriesData(
  data: IOpinionBarAndLineSeriesData,
): IOpinionBarAndLineSeriesData {
  const { xAxisData, series } = data;

  return {
    xAxisData,
    series: series.map((item, index) => ({
      ...item,
      key: item.key || createSeriesKey(item.name, index),
      color: item.color || DEFAULT_SERIES_COLORS[index % DEFAULT_SERIES_COLORS.length],
    })),
  };
}

function flatDataToSeriesData(
  flatData: IOpinionBarAndLineFlatDatum[],
): IOpinionBarAndLineSeriesData {
  const xAxisSet = new Set<string>();
  const seriesMap = new Map<
    string,
    { name: string; color?: string; valueMap: Map<string, number | null> }
  >();

  flatData.forEach(({ date, name, value, color }) => {
    xAxisSet.add(date);

    if (!seriesMap.has(name)) {
      seriesMap.set(name, { name, color, valueMap: new Map() });
    }

    const seriesItem = seriesMap.get(name)!;
    if (color && !seriesItem.color) {
      seriesItem.color = color;
    }
    seriesItem.valueMap.set(date, value);
  });

  const xAxisData = Array.from(xAxisSet);

  const series: IOpinionBarAndLineSeriesItem[] = Array.from(seriesMap.values()).map(
    (item, index) => ({
      name: item.name,
      key: createSeriesKey(item.name, index),
      color: item.color || DEFAULT_SERIES_COLORS[index % DEFAULT_SERIES_COLORS.length],
      values: xAxisData.map((date) =>
        item.valueMap.has(date) ? item.valueMap.get(date)! : null,
      ),
    }),
  );

  return { xAxisData, series };
}

function buildAdaptedData(
  seriesData: IOpinionBarAndLineSeriesData,
): IAdaptedOpinionLineChartData | null {
  const { xAxisData, series } = seriesData;

  if (!xAxisData?.length || !series?.length) {
    return null;
  }

  const yAxisData: Record<string, (number | null)[]> = {};
  const customSeriesList: ChartOptionsMapType = [];

  series.forEach((item, index) => {
    const key = item.key || createSeriesKey(item.name, index);
    const color = item.color || DEFAULT_SERIES_COLORS[index % DEFAULT_SERIES_COLORS.length];

    yAxisData[key] = item.values;
    customSeriesList.push({
      name: item.name,
      key: key as ChartOptionsMapType[number]['key'],
      color,
    });
  });

  return {
    xAxisData,
    yAxisData,
    customSeriesList,
  };
}

function getXAxisDataFromLineOption(lineOption: IRecord): string[] {
  const xAxis = lineOption?.xAxis;
  if (Array.isArray(xAxis)) {
    return (xAxis[0]?.data || []).map((item: any) => String(item));
  }
  return (xAxis?.data || []).map((item: any) => String(item));
}

function getSeriesColor(
  series: IRecord,
  index: number,
  lineOption?: IRecord,
): string {
  const color = series?.itemStyle?.color || series?.lineStyle?.color;
  if (typeof color === 'string' && color.trim()) {
    return color;
  }

  const palette = Array.isArray(lineOption?.color)
    ? lineOption.color
    : DEFAULT_SERIES_COLORS;

  return palette[index % palette.length];
}

function normalizeNumericValue(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function extractSeriesPoint(point: unknown): {
  time?: string | number;
  value: unknown;
} | null {
  if (Array.isArray(point)) {
    const [time, value] = point;
    return { time, value };
  }

  if (point && typeof point === 'object' && 'value' in point) {
    const rawValue = (point as { value?: unknown }).value;
    if (Array.isArray(rawValue)) {
      const [time, value] = rawValue;
      return { time, value };
    }
    return { value: rawValue };
  }

  return { value: point };
}

function hasTupleSeriesData(data: unknown[]): boolean {
  return data.some((item) => {
    if (Array.isArray(item)) {
      return true;
    }

    const point = extractSeriesPoint(item);
    return point?.time !== undefined && point?.time !== null;
  });
}

function seriesDataToValues(
  data: any[] | undefined,
  xAxisData: string[],
): (number | null)[] {
  if (!Array.isArray(data) || data.length === 0) {
    return xAxisData.map(() => null);
  }

  const extractValuesByIndex = (source: any[], length: number) =>
    Array.from({ length }, (_, index) => {
      const parsed = extractSeriesPoint(source[index]);
      return normalizeNumericValue(parsed?.value);
    });

  if (data.length === xAxisData.length) {
    const values = extractValuesByIndex(data, xAxisData.length);
    if (values.some((value) => value !== null)) {
      return values;
    }
  }

  if (hasTupleSeriesData(data)) {
    const valueMap = new Map<string, number | null>();
    data.forEach((point) => {
      const parsed = extractSeriesPoint(point);
      if (!parsed || parsed.time === undefined || parsed.time === null) {
        return;
      }
      valueMap.set(String(parsed.time), normalizeNumericValue(parsed.value));
    });
    const mapped = xAxisData.map((date) =>
      valueMap.has(date) ? valueMap.get(date)! : null,
    );
    if (mapped.some((value) => value !== null)) {
      return mapped;
    }
  }

  if (data.length === xAxisData.length) {
    return extractValuesByIndex(data, xAxisData.length);
  }

  return extractValuesByIndex(data, xAxisData.length);
}

/**
 * 将情报侧折线数据转换为舆情 BarAndLineCharts 所需格式
 */
export function adaptLineChartData(
  data: IOpinionBarAndLineChartData | null | undefined,
): IAdaptedOpinionLineChartData | null {
  if (!data) {
    return null;
  }

  const seriesData = isSeriesData(data)
    ? normalizeSeriesData(data)
    : flatDataToSeriesData(data);

  return buildAdaptedData(seriesData);
}

/**
 * 将 useLineCharts 产出的 lineOption 转换为舆情 BarAndLineCharts 所需格式
 */
export function adaptLineOption(
  lineOption: IRecord | null | undefined,
): IAdaptedOpinionLineChartData | null {
  if (!lineOption?.series?.length) {
    return null;
  }

  const xAxisData = getXAxisDataFromLineOption(lineOption);
  if (!xAxisData.length) {
    return null;
  }

  const lineSeries = (lineOption.series as IRecord[]).filter(
    (item) =>
      item?.type === 'line' &&
      item?.name &&
      !IGNORED_SERIES_NAMES.includes(String(item.name)),
  );

  if (!lineSeries.length) {
    return null;
  }

  const yAxisData: Record<string, (number | null)[]> = {};
  const customSeriesList: ChartOptionsMapType = [];

  lineSeries.forEach((series, index) => {
    const key = createSeriesKey(String(series.name), index);
    const color = getSeriesColor(series, index, lineOption);

    yAxisData[key] = seriesDataToValues(series.data as any[], xAxisData);
    customSeriesList.push({
      name: String(series.name),
      key: key as ChartOptionsMapType[number]['key'],
      color,
    });
  });

  return {
    xAxisData,
    yAxisData,
    customSeriesList,
  };
}

export function shouldUseOpinionLineChart(
  chartType?: string,
  lineOption?: IRecord | null,
): boolean {
  if (chartType !== 'line' && chartType !== 'stackedLine') {
    return false;
  }
  if (!lineOption?.series?.length) {
    return false;
  }

  const lineSeries = (lineOption.series as IRecord[]).filter(
    (item) =>
      item?.type === 'line' &&
      item?.name &&
      !IGNORED_SERIES_NAMES.includes(String(item.name)),
  );

  return lineSeries.length > 0;
}
